import type { WebHostingRouterType } from '@nucleus/web-hosting';
import { useQueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import React, { useMemo } from 'react';
import { useUser } from '../components/user/UserProvider';

const {
  TRPCProvider,
  useTRPC: useWebHostingApi,
  useTRPCClient: useWebHostingTrpcClient,
} = createTRPCContext<WebHostingRouterType>();

export { useWebHostingApi, useWebHostingTrpcClient };

const getServerBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    return '';
  }
  return axios.defaults.baseURL ?? '';
};

interface WebHostingTrpcProviderProps {
  children: React.ReactNode;
}

export const WebHostingTrpcProvider = ({ children }: WebHostingTrpcProviderProps): JSX.Element => {
  const queryClient = useQueryClient();
  const { jwtToken } = useUser();
  const isUserAuthenticated = jwtToken !== undefined;

  const trpcClient = useMemo(() => {
    const apiPrefix = isUserAuthenticated ? '/_api/private' : '/_api/public';

    return createTRPCClient<WebHostingRouterType>({
      links: [
        httpBatchLink({
          url: `${getServerBaseUrl()}${apiPrefix}/trpc`,
          maxURLLength: 2048,
          headers: async () => {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            return token !== undefined && token !== '' ? { Authorization: token } : {};
          },
        }),
      ],
    });
  }, [isUserAuthenticated]);

  return (
    <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
      {children}
    </TRPCProvider>
  );
};
